import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { auth } from '../lib/siteAuth';
import SeoHead from '../components/SeoHead';

export default function AdminBookingsPage() {
  const [user, setUser] = useState(auth.user);
  const [authChecked, setAuthChecked] = useState(false);
  const [emailInput, setEmailInput] = useState('admin@eeskole.dk');
  const [passwordInput, setPasswordInput] = useState('');
  const [loginError, setLoginError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  // Data states
  const [bookings, setBookings] = useState([]);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState(null);

  // Recurring scheduler states
  const [recStartDate, setRecStartDate] = useState(() => {
    const d = new Date();
    return d.toISOString().split('T')[0];
  });
  const [recEndDate, setRecEndDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 56); // 8 weeks ahead
    return d.toISOString().split('T')[0];
  });
  const [recWeekdays, setRecWeekdays] = useState([1, 2, 3, 4, 5]); // Man-Fre
  const [recTimes, setRecTimes] = useState(['10:00', '13:00', '15:30']);
  const [customRecTime, setCustomRecTime] = useState('');
  const [isGeneratingRecurring, setIsGeneratingRecurring] = useState(false);

  // Single date/slot inputs
  const [newDate, setNewDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().split('T')[0];
  });
  const [newTime, setNewTime] = useState('14:00');
  const [isSavingSlot, setIsSavingSlot] = useState(false);

  // Filters & search
  const [statusFilter, setStatusFilter] = useState('all');
  const [slotSearch, setSlotSearch] = useState('');
  const [activeTab, setActiveTab] = useState('recurring'); // 'recurring' | 'single'

  useEffect(() => {
    auth.init().then(() => {
      setUser(auth.user);
      setAuthChecked(true);
    }).catch(() => {
      setAuthChecked(true);
    });
    const unsub = auth.onChange((u) => {
      setUser(u);
      setAuthChecked(true);
    });
    return unsub;
  }, []);

  const loadData = async () => {
    if (!user) {
      setBookings([]);
      setSlots([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await auth.fetch('/_fn/bookings-admin', { method: 'GET' });
      const data = await res.json();
      if (data.success) {
        setBookings(data.bookings || []);
        setSlots(data.slots || []);
      }
    } catch (err) {
      console.error('Fejl ved indlæsning af bookinger:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [user]);

  const showNotification = (msg, type = 'success') => {
    setNotification({ msg, type });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setIsLoggingIn(true);
    try {
      const loggedUser = await auth.login(emailInput.trim(), passwordInput);
      setUser(loggedUser);
      showNotification(`Velkommen, ${loggedUser.name || loggedUser.email}!`);
      loadData();
    } catch (err) {
      setLoginError(err.message || 'Forkert e-mail eller adgangskode');
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleLogout = async () => {
    await auth.logout();
    setUser(null);
    setBookings([]);
    setSlots([]);
    showNotification('Du er nu logget ud.');
  };

  // Helper to toggle weekday in recurring
  const toggleWeekday = (dayIndex) => {
    if (recWeekdays.includes(dayIndex)) {
      if (recWeekdays.length === 1) return; // Keep at least one
      setRecWeekdays(recWeekdays.filter((d) => d !== dayIndex));
    } else {
      setRecWeekdays([...recWeekdays, dayIndex].sort());
    }
  };

  // Helper to add custom time in recurring
  const handleAddCustomTime = (e) => {
    e.preventDefault();
    const t = customRecTime.trim();
    if (!/^\d{2}:\d{2}$/.test(t)) {
      showNotification('Angiv tidspunkt i formatet TT:MM (f.eks. 14:30)', 'error');
      return;
    }
    if (recTimes.includes(t)) {
      showNotification('Tidspunktet er allerede tilføjet', 'error');
      return;
    }
    setRecTimes([...recTimes, t].sort());
    setCustomRecTime('');
  };

  // Quick period helper
  const setPeriodWeeks = (weeks) => {
    const start = new Date(recStartDate || new Date());
    const end = new Date(start);
    end.setDate(end.getDate() + weeks * 7);
    setRecEndDate(end.toISOString().split('T')[0]);
  };

  // Create Recurring Slots
  const handleCreateRecurringSlots = async (e) => {
    e.preventDefault();
    if (!recStartDate || !recEndDate) {
      showNotification('Vælg venligst både start- og slutdato', 'error');
      return;
    }
    if (recWeekdays.length === 0) {
      showNotification('Vælg mindst én ugedag', 'error');
      return;
    }
    if (recTimes.length === 0) {
      showNotification('Vælg mindst ét tidspunkt', 'error');
      return;
    }

    setIsGeneratingRecurring(true);
    try {
      const res = await auth.fetch('/_fn/bookings-admin', {
        method: 'POST',
        body: JSON.stringify({
          action: 'create_recurring_slots',
          start_date: recStartDate,
          end_date: recEndDate,
          weekdays: recWeekdays,
          times: recTimes,
          max_capacity: 1,
        }),
      });
      const data = await res.json();
      if (data.success) {
        showNotification(data.message || 'Tidsplan er oprettet!');
        loadData();
      } else {
        showNotification(data.error || 'Fejl under generering af tider', 'error');
      }
    } catch (err) {
      showNotification('Netværksfejl under oprettelse af tidsplan', 'error');
    } finally {
      setIsGeneratingRecurring(false);
    }
  };

  // Single slot create
  const handleCreateSlot = async (e) => {
    e.preventDefault();
    if (!newDate || !newTime) return;
    setIsSavingSlot(true);
    try {
      const res = await auth.fetch('/_fn/bookings-admin', {
        method: 'POST',
        body: JSON.stringify({
          action: 'create_slot',
          slot_date: newDate,
          slot_time: newTime,
        }),
      });
      const data = await res.json();
      if (data.success) {
        showNotification(data.message || 'Tidspunkt tilføjet');
        loadData();
      } else {
        showNotification(data.error || 'Kunne ikke tilføje tidspunkt', 'error');
      }
    } catch (err) {
      showNotification('Netværksfejl ved oprettelse', 'error');
    } finally {
      setIsSavingSlot(false);
    }
  };

  // Single date default times (3 standard times)
  const handleAddDefaultTimes = async () => {
    if (!newDate) return;
    setIsSavingSlot(true);
    try {
      const res = await auth.fetch('/_fn/bookings-admin', {
        method: 'POST',
        body: JSON.stringify({
          action: 'seed_day_slots',
          slot_date: newDate,
          times: ['10:00', '13:00', '15:30'],
        }),
      });
      const data = await res.json();
      if (data.success) {
        showNotification(`Standardtider (10:00, 13:00, 15:30) er tilføjet for ${newDate}`);
        loadData();
      } else {
        showNotification(data.error || 'Fejl', 'error');
      }
    } catch (err) {
      showNotification('Netværksfejl', 'error');
    } finally {
      setIsSavingSlot(false);
    }
  };

  // Delete single slot
  const handleDeleteSlot = async (slotId, date, time) => {
    if (!window.confirm(`Er du sikker på, at du vil fjerne tidspunktet kl. ${time} den ${date}?`)) return;
    try {
      const res = await auth.fetch('/_fn/bookings-admin', {
        method: 'POST',
        body: JSON.stringify({
          action: 'delete_slot',
          slotId,
        }),
      });
      const data = await res.json();
      if (data.success) {
        showNotification('Tidspunktet er slettet.');
        loadData();
      }
    } catch (err) {
      showNotification('Kunne ikke slette tidspunkt', 'error');
    }
  };

  // Delete all unbooked slots on a single date
  const handleDeleteDaySlots = async (date) => {
    if (!window.confirm(`Vil du fjerne alle ledige tider på ${date}? (Eventuelle aktive bookinger bevares)`)) return;
    try {
      const res = await auth.fetch('/_fn/bookings-admin', {
        method: 'POST',
        body: JSON.stringify({
          action: 'delete_day_slots',
          slot_date: date,
        }),
      });
      const data = await res.json();
      if (data.success) {
        showNotification(data.message || `Tider for ${date} er slettet.`);
        loadData();
      }
    } catch (err) {
      showNotification('Kunne ikke slette tider for datoen', 'error');
    }
  };

  // Block slot toggle
  const handleBlockSlot = async (date, time) => {
    try {
      const res = await auth.fetch('/_fn/bookings-admin', {
        method: 'POST',
        body: JSON.stringify({
          action: 'block_slot',
          slot_date: date,
          slot_time: time,
        }),
      });
      const data = await res.json();
      if (data.success) {
        showNotification(`Tidspunkt ${time} den ${date} er nu blokeret for bookinger.`);
        loadData();
      }
    } catch (err) {
      showNotification('Fejl ved blokering af tidspunkt', 'error');
    }
  };

  // Cancel booking
  const handleCancelBooking = async (bookingId) => {
    if (!window.confirm('Vil du aflyse denne booking? Tiden frigives automatisk til andre forældre i kalenderen.')) return;
    try {
      const res = await auth.fetch('/_fn/bookings-admin', {
        method: 'POST',
        body: JSON.stringify({
          action: 'cancel_booking',
          bookingId,
        }),
      });
      const data = await res.json();
      if (data.success) {
        showNotification('Booking er markeret som aflyst.');
        loadData();
      }
    } catch (err) {
      showNotification('Kunne ikke aflyse booking', 'error');
    }
  };

  // Confirm booking
  const handleConfirmBooking = async (bookingId) => {
    try {
      const res = await auth.fetch('/_fn/bookings-admin', {
        method: 'POST',
        body: JSON.stringify({
          action: 'confirm_booking',
          bookingId,
        }),
      });
      const data = await res.json();
      if (data.success) {
        showNotification('Booking er bekræftet.');
        loadData();
      }
    } catch (err) {
      showNotification('Fejl', 'error');
    }
  };

  // Calculate live preview of recurring generator
  const recurringPreview = useMemo(() => {
    if (!recStartDate || !recEndDate) return null;
    const start = new Date(recStartDate + 'T00:00:00Z');
    const end = new Date(recEndDate + 'T00:00:00Z');
    if (isNaN(start.getTime()) || isNaN(end.getTime()) || end < start) return null;

    let matchingDays = 0;
    for (let d = new Date(start); d <= end; d.setUTCDate(d.getUTCDate() + 1)) {
      if (recWeekdays.includes(d.getUTCDay())) matchingDays++;
    }
    const totalSlots = matchingDays * recTimes.length;
    return { matchingDays, totalSlots };
  }, [recStartDate, recEndDate, recWeekdays, recTimes]);

  // Group slots by date for intuitive overview
  const groupedSlots = useMemo(() => {
    const map = {};
    for (const s of slots) {
      if (!map[s.slot_date]) map[s.slot_date] = [];
      map[s.slot_date].push(s);
    }
    const dates = Object.keys(map).sort();
    return dates
      .filter((d) => !slotSearch || d.includes(slotSearch))
      .map((d) => ({
        date: d,
        slots: map[d].sort((a, b) => a.slot_time.localeCompare(b.slot_time)),
      }));
  }, [slots, slotSearch]);

  const filteredBookings = bookings.filter((b) => {
    if (statusFilter === 'active') return b.status !== 'cancelled';
    if (statusFilter === 'cancelled') return b.status === 'cancelled';
    return true;
  });

  const weekdayLabels = [
    { id: 1, label: 'Mandag', short: 'Man' },
    { id: 2, label: 'Tirsdag', short: 'Tir' },
    { id: 3, label: 'Onsdag', short: 'Ons' },
    { id: 4, label: 'Torsdag', short: 'Tor' },
    { id: 5, label: 'Fredag', short: 'Fre' },
    { id: 6, label: 'Lørdag', short: 'Lør' },
    { id: 0, label: 'Søndag', short: 'Søn' },
  ];

  return (
    <div className="bg-[#f7f9f9] min-h-screen py-10 px-4 sm:px-6 lg:px-8">
      <SeoHead
        title="Administration af rundvisninger - Emmerske Efterskole"
        description="Internt administrationsmodul for Emmerske Efterskoles rundvisningskalender og bookinger."
      />

      <div className="max-w-7xl mx-auto">
        {/* Top Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 sm:p-8 mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-[#007a53]/10 text-[#007a53]">
                <i className="fa-solid fa-calendar-check text-xl"></i>
              </span>
              <div>
                <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
                  Administration af rundvisninger
                </h1>
                <p className="text-slate-600 text-sm mt-0.5">
                  Emmerske Efterskole · Kalenderstyring, ugeplan og tilmeldinger
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Link
              to="/book-rundvisning"
              target="_blank"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-300 text-slate-700 bg-white hover:bg-slate-50 text-sm font-semibold transition-colors"
            >
              <i className="fa-solid fa-arrow-up-right-from-square text-xs"></i>
              Se offentlig booking
            </Link>

            {user ? (
              <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-xl px-4 py-2">
                <div className="text-xs">
                  <div className="font-bold text-slate-800">{user.name || user.email}</div>
                  <div className="text-emerald-600 font-medium flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                    Logget ind
                  </div>
                </div>
                <button
                  onClick={handleLogout}
                  className="text-xs font-semibold text-rose-600 hover:text-rose-700 underline ml-2 cursor-pointer"
                >
                  Log ud
                </button>
              </div>
            ) : null}
          </div>
        </div>

        {/* Notifications */}
        {notification && (
          <div
            className={`mb-6 p-4 rounded-xl flex items-center gap-3 border shadow-sm ${
              notification.type === 'error'
                ? 'bg-rose-50 border-rose-200 text-rose-800'
                : 'bg-emerald-50 border-emerald-200 text-emerald-800'
            }`}
          >
            <i
              className={`fa-solid ${
                notification.type === 'error' ? 'fa-circle-exclamation' : 'fa-circle-check'
              }`}
            ></i>
            <span className="font-medium text-sm">{notification.msg}</span>
          </div>
        )}

        {/* Login modal/panel if not logged in */}
        {!authChecked ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center max-w-md mx-auto shadow-sm">
            <div className="inline-block animate-spin w-8 h-8 border-4 border-[#007a53] border-t-transparent rounded-full mb-4"></div>
            <p className="text-slate-600 font-medium text-sm">Kontrollerer adgang...</p>
          </div>
        ) : !user ? (
          <div className="bg-white border border-slate-200 rounded-3xl p-8 sm:p-10 mb-8 max-w-lg mx-auto shadow-sm">
            <div className="text-center mb-6">
              <div className="w-14 h-14 rounded-2xl bg-[#007a53]/10 text-[#007a53] flex items-center justify-center mx-auto mb-4">
                <i className="fa-solid fa-shield-halved text-2xl"></i>
              </div>
              <h2 className="text-2xl font-extrabold text-slate-900 tracking-tight">Log ind på administrationen</h2>
              <p className="text-sm text-slate-600 mt-1.5">
                Indtast dine administrative adgangsoplysninger for at få adgang til rundvisningskalenderen og bookinger.
              </p>
            </div>

            {loginError && (
              <div className="mb-5 p-3.5 rounded-xl bg-rose-50 border border-rose-200 text-rose-800 text-xs font-semibold flex items-center gap-2">
                <i className="fa-solid fa-circle-exclamation text-rose-500"></i>
                <span>{loginError}</span>
              </div>
            )}

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">E-mail</label>
                <div className="relative">
                  <i className="fa-regular fa-envelope absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm"></i>
                  <input
                    type="email"
                    required
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    className="w-full pl-10 pr-3.5 py-2.5 rounded-xl border border-slate-300 bg-white text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-[#007a53]"
                    placeholder="admin@eeskole.dk"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">Adgangskode</label>
                <div className="relative">
                  <i className="fa-solid fa-lock absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm"></i>
                  <input
                    type="password"
                    required
                    value={passwordInput}
                    onChange={(e) => setPasswordInput(e.target.value)}
                    className="w-full pl-10 pr-3.5 py-2.5 rounded-xl border border-slate-300 bg-white text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-[#007a53]"
                    placeholder="••••••••"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoggingIn}
                className="w-full py-3 rounded-xl bg-[#007a53] hover:bg-[#006343] text-white font-bold text-sm shadow transition-colors disabled:opacity-50 cursor-pointer flex items-center justify-center gap-2"
              >
                {isLoggingIn ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Logger ind...</span>
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-arrow-right-to-bracket text-xs"></i>
                    <span>Log ind</span>
                  </>
                )}
              </button>
            </form>
          </div>
        ) : (
          /* Main Dashboard Layout kun for indloggede */
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Left Column: Manage Dates & Recurring slots (5 cols) */}
            <div className="lg:col-span-5 space-y-6">
              {/* Creator Card with Tabs: Recurring vs. Single */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                <div className="flex items-center justify-between pb-4 border-b border-slate-100 mb-5">
                  <div className="flex items-center gap-2">
                    <span className="w-7 h-7 rounded-lg bg-[#007a53]/10 text-[#007a53] flex items-center justify-center text-sm font-bold">
                      <i className="fa-solid fa-wand-magic-sparkles"></i>
                    </span>
                    <h2 className="text-lg font-bold text-slate-900">
                      Opret rundvisningstider
                    </h2>
                  </div>

                  <div className="flex bg-slate-100 p-1 rounded-xl text-xs font-semibold">
                    <button
                      type="button"
                      onClick={() => setActiveTab('recurring')}
                      className={`px-3 py-1.5 rounded-lg transition-all ${
                        activeTab === 'recurring'
                          ? 'bg-white text-[#007a53] shadow-sm font-bold'
                          : 'text-slate-600 hover:text-slate-900'
                      }`}
                    >
                      Ugeplan (Fast)
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveTab('single')}
                      className={`px-3 py-1.5 rounded-lg transition-all ${
                        activeTab === 'single'
                          ? 'bg-white text-[#007a53] shadow-sm font-bold'
                          : 'text-slate-600 hover:text-slate-900'
                      }`}
                    >
                      Enkelt dato
                    </button>
                  </div>
                </div>

                {activeTab === 'recurring' ? (
                  /* Recurring Schedule Generator */
                  <form onSubmit={handleCreateRecurringSlots} className="space-y-5">
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <label className="text-xs font-bold text-slate-700">Periode for tidsplanen</label>
                        <div className="flex items-center gap-1.5 text-[11px]">
                          <button
                            type="button"
                            onClick={() => setPeriodWeeks(4)}
                            className="px-2 py-0.5 rounded bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium"
                          >
                            +4 uger
                          </button>
                          <button
                            type="button"
                            onClick={() => setPeriodWeeks(8)}
                            className="px-2 py-0.5 rounded bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium"
                          >
                            +8 uger
                          </button>
                          <button
                            type="button"
                            onClick={() => setPeriodWeeks(12)}
                            className="px-2 py-0.5 rounded bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium"
                          >
                            +12 uger
                          </button>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <span className="text-[11px] text-slate-500 block mb-0.5">Fra dato:</span>
                          <input
                            type="date"
                            required
                            value={recStartDate}
                            onChange={(e) => setRecStartDate(e.target.value)}
                            className="w-full px-3 py-2 rounded-xl border border-slate-300 bg-white text-slate-900 text-xs focus:outline-none focus:ring-2 focus:ring-[#007a53]"
                          />
                        </div>
                        <div>
                          <span className="text-[11px] text-slate-500 block mb-0.5">Til dato:</span>
                          <input
                            type="date"
                            required
                            value={recEndDate}
                            onChange={(e) => setRecEndDate(e.target.value)}
                            className="w-full px-3 py-2 rounded-xl border border-slate-300 bg-white text-slate-900 text-xs focus:outline-none focus:ring-2 focus:ring-[#007a53]"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Weekday Selector */}
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <label className="text-xs font-bold text-slate-700">Aktive ugedage</label>
                        <div className="flex items-center gap-1.5 text-[11px]">
                          <button
                            type="button"
                            onClick={() => setRecWeekdays([1, 2, 3, 4, 5])}
                            className="text-[#007a53] hover:underline font-semibold"
                          >
                            Alle hverdage (Man-Fre)
                          </button>
                          <span className="text-slate-300">·</span>
                          <button
                            type="button"
                            onClick={() => setRecWeekdays([1, 2, 3, 4])}
                            className="text-[#007a53] hover:underline font-semibold"
                          >
                            Man-Tor
                          </button>
                        </div>
                      </div>

                      <div className="grid grid-cols-7 gap-1">
                        {weekdayLabels.map((w) => {
                          const isSelected = recWeekdays.includes(w.id);
                          return (
                            <button
                              key={w.id}
                              type="button"
                              onClick={() => toggleWeekday(w.id)}
                              className={`py-2 rounded-xl text-xs font-bold transition-all text-center ${
                                isSelected
                                  ? 'bg-[#007a53] text-white shadow-sm'
                                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                              }`}
                              title={w.label}
                            >
                              {w.short}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Times Selector */}
                    <div>
                      <label className="text-xs font-bold text-slate-700 block mb-1.5">
                        Tidspunkter på de valgte dage
                      </label>

                      {/* Active Time Chips */}
                      <div className="flex flex-wrap gap-1.5 mb-2.5">
                        {recTimes.map((t) => (
                          <span
                            key={t}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#007a53]/10 text-[#007a53] font-mono font-bold text-xs"
                          >
                            <span>kl. {t}</span>
                            <button
                              type="button"
                              onClick={() => setRecTimes(recTimes.filter((x) => x !== t))}
                              className="text-[#007a53]/70 hover:text-rose-600 cursor-pointer text-xs"
                              title="Fjern tidspunkt"
                            >
                              <i className="fa-solid fa-xmark"></i>
                            </button>
                          </span>
                        ))}
                      </div>

                      {/* Quick Presets & Custom Input */}
                      <div className="flex flex-wrap items-center gap-1.5 mb-2">
                        {['10:00', '13:00', '14:00', '15:30', '16:30'].map((preset) => {
                          const isAdded = recTimes.includes(preset);
                          return (
                            <button
                              key={preset}
                              type="button"
                              disabled={isAdded}
                              onClick={() => setRecTimes([...recTimes, preset].sort())}
                              className={`px-2 py-1 rounded text-[11px] font-semibold transition-colors ${
                                isAdded
                                  ? 'bg-slate-100 text-slate-400 cursor-default'
                                  : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                              }`}
                            >
                              + {preset}
                            </button>
                          );
                        })}
                      </div>

                      {/* Custom Time Add Form */}
                      <div className="flex gap-2">
                        <input
                          type="time"
                          value={customRecTime}
                          onChange={(e) => setCustomRecTime(e.target.value)}
                          className="px-3 py-1.5 rounded-xl border border-slate-300 text-xs text-slate-900 bg-white focus:outline-none focus:ring-2 focus:ring-[#007a53]"
                        />
                        <button
                          type="button"
                          onClick={handleAddCustomTime}
                          className="px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-bold transition-colors"
                        >
                          + Tilføj tidspunkt
                        </button>
                      </div>
                    </div>

                    {/* Summary Preview Box */}
                    {recurringPreview && (
                      <div className="p-3.5 rounded-xl bg-emerald-50/70 border border-emerald-200 text-emerald-900 text-xs">
                        <div className="flex items-center gap-2 font-bold mb-0.5">
                          <i className="fa-solid fa-check-circle text-emerald-600"></i>
                          <span>Oversigt over tidsplanen:</span>
                        </div>
                        <p>
                          Opretter/sikrer <strong>{recurringPreview.totalSlots} tider</strong> fordelt på{' '}
                          <strong>{recurringPreview.matchingDays} datoer</strong> i perioden (kl.{' '}
                          {recTimes.join(', ')}).
                        </p>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={isGeneratingRecurring || !recurringPreview || recurringPreview.totalSlots === 0}
                      className="w-full py-3 px-4 rounded-xl bg-[#007a53] hover:bg-[#006343] text-white font-bold text-sm shadow transition-colors disabled:opacity-50 flex items-center justify-center gap-2 cursor-pointer"
                    >
                      {isGeneratingRecurring ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          <span>Opretter tidsplan i databasen...</span>
                        </>
                      ) : (
                        <>
                          <i className="fa-solid fa-calendar-plus text-xs"></i>
                          <span>⚡ Opret / opdater ugeplan for perioden</span>
                        </>
                      )}
                    </button>
                  </form>
                ) : (
                  /* Single Slot Form */
                  <form onSubmit={handleCreateSlot} className="space-y-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-1">Dato for rundvisning</label>
                      <input
                        type="date"
                        required
                        value={newDate}
                        onChange={(e) => setNewDate(e.target.value)}
                        className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 bg-white text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-[#007a53]"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-bold text-slate-700 mb-1">Tidspunkt</label>
                        <input
                          type="time"
                          required
                          value={newTime}
                          onChange={(e) => setNewTime(e.target.value)}
                          className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 bg-white text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-[#007a53]"
                        />
                      </div>
                      <div className="flex items-end">
                        <button
                          type="submit"
                          disabled={isSavingSlot}
                          className="w-full py-2.5 px-3 rounded-xl bg-[#007a53] hover:bg-[#006343] text-white font-bold text-sm shadow transition-colors disabled:opacity-50"
                        >
                          {isSavingSlot ? 'Gemmer...' : '+ Tilføj tid'}
                        </button>
                      </div>
                    </div>

                    <div className="pt-2 border-t border-slate-100">
                      <button
                        type="button"
                        onClick={handleAddDefaultTimes}
                        disabled={isSavingSlot}
                        className="w-full py-2 px-3 rounded-xl border border-[#007a53]/30 text-[#007a53] bg-[#007a53]/5 hover:bg-[#007a53]/10 text-xs font-bold transition-colors"
                      >
                        + Tilføj 3 standardtider på datoen (10:00, 13:00, 15:30)
                      </button>
                    </div>
                  </form>
                )}
              </div>

              {/* Grouped List of Configured Slots */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2">
                      <i className="fa-solid fa-list-check text-slate-500"></i>
                      Aktive datoer & tider ({slots.length} tider)
                    </h3>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      Kun datoer oprettet herover er synlige i bookingkalenderen.
                    </p>
                  </div>
                  <button
                    onClick={loadData}
                    className="text-xs text-[#007a53] hover:underline font-semibold"
                  >
                    Opdater
                  </button>
                </div>

                {/* Search filter */}
                {slots.length > 5 && (
                  <div className="mb-3">
                    <input
                      type="text"
                      placeholder="Filtrér efter dato (fx 2026-10)..."
                      value={slotSearch}
                      onChange={(e) => setSlotSearch(e.target.value)}
                      className="w-full px-3 py-1.5 rounded-lg border border-slate-200 text-xs bg-slate-50 focus:bg-white focus:outline-none focus:ring-1 focus:ring-[#007a53]"
                    />
                  </div>
                )}

                {groupedSlots.length === 0 ? (
                  <div className="p-5 text-center rounded-xl bg-slate-50 border border-dashed border-slate-200 text-slate-500 text-xs">
                    <i className="fa-regular fa-calendar-xmark text-2xl text-slate-400 mb-2 block"></i>
                    Der er p.t. ingen tider oprettet i databasen. Benyt ugeplanlæggeren herover for nemt at oprette faste tider på alle hverdage i en ønsket periode.
                  </div>
                ) : (
                  <div className="space-y-3 max-h-[500px] overflow-y-auto pr-1">
                    {groupedSlots.map((group) => {
                      const [y, m, d] = group.date.split('-').map(Number);
                      const dObj = new Date(y, m - 1, d);
                      const daysOfWeek = ['Søndag', 'Mandag', 'Tirsdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lørdag'];
                      const monthNames = ['jan.', 'feb.', 'mar.', 'apr.', 'maj', 'jun.', 'jul.', 'aug.', 'sep.', 'okt.', 'nov.', 'dec.'];
                      const dateTitle = `${daysOfWeek[dObj.getDay()]} d. ${d}. ${monthNames[m - 1]}`;

                      return (
                        <div
                          key={group.date}
                          className="p-3.5 rounded-xl border border-slate-200/80 bg-slate-50/70 hover:bg-slate-50 transition-colors"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="font-bold text-xs text-slate-900 flex items-center gap-1.5">
                              <i className="fa-regular fa-calendar text-slate-400 text-xs"></i>
                              <span>{dateTitle}</span>
                              <span className="text-[10px] text-slate-400 font-mono font-normal">({group.date})</span>
                            </div>

                            <button
                              type="button"
                              onClick={() => handleDeleteDaySlots(group.date)}
                              title="Slet alle ledige tider på denne dato"
                              className="text-[11px] text-rose-600 hover:text-rose-800 hover:underline font-semibold"
                            >
                              Slet dags tider
                            </button>
                          </div>

                          <div className="flex flex-wrap gap-1.5">
                            {group.slots.map((s) => (
                              <div
                                key={s.id}
                                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-mono font-bold border ${
                                  s.is_active
                                    ? 'bg-white border-slate-200 text-slate-800'
                                    : 'bg-amber-50 border-amber-200 text-amber-800'
                                }`}
                              >
                                <span>kl. {s.slot_time}</span>
                                {s.is_active ? (
                                  <button
                                    onClick={() => handleBlockSlot(s.slot_date, s.slot_time)}
                                    title="Bloker tidspunkt"
                                    className="text-slate-400 hover:text-amber-600 cursor-pointer"
                                  >
                                    <i className="fa-solid fa-ban text-[10px]"></i>
                                  </button>
                                ) : (
                                  <span className="text-[10px] text-amber-600 uppercase font-sans font-semibold">Blokeret</span>
                                )}
                                <button
                                  onClick={() => handleDeleteSlot(s.id, s.slot_date, s.slot_time)}
                                  title="Fjern tidspunkt"
                                  className="text-slate-400 hover:text-rose-600 cursor-pointer"
                                >
                                  <i className="fa-solid fa-trash-can text-[10px]"></i>
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Right Column: Bookings Overview (7 cols) */}
            <div className="lg:col-span-7">
              <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 shadow-sm">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                  <div>
                    <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                      <i className="fa-solid fa-users text-[#007a53]"></i>
                      Indkomne tilmeldinger ({bookings.length})
                    </h2>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Oversigt over familier, som har booket en rundvisning
                    </p>
                  </div>

                  {/* Filter */}
                  <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-xl">
                    <button
                      onClick={() => setStatusFilter('all')}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                        statusFilter === 'all'
                          ? 'bg-white text-slate-900 shadow-sm'
                          : 'text-slate-600 hover:text-slate-900'
                      }`}
                    >
                      Alle ({bookings.length})
                    </button>
                    <button
                      onClick={() => setStatusFilter('active')}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                        statusFilter === 'active'
                          ? 'bg-white text-[#007a53] shadow-sm'
                          : 'text-slate-600 hover:text-slate-900'
                      }`}
                    >
                      Aktive ({bookings.filter((b) => b.status !== 'cancelled').length})
                    </button>
                    <button
                      onClick={() => setStatusFilter('cancelled')}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                        statusFilter === 'cancelled'
                          ? 'bg-white text-rose-700 shadow-sm'
                          : 'text-slate-600 hover:text-slate-900'
                      }`}
                    >
                      Aflyste ({bookings.filter((b) => b.status === 'cancelled').length})
                    </button>
                  </div>
                </div>

                {loading ? (
                  <div className="py-12 text-center text-slate-400 text-sm">
                    <div className="w-6 h-6 border-2 border-[#007a53] border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                    Indlæser tilmeldinger...
                  </div>
                ) : filteredBookings.length === 0 ? (
                  <div className="py-12 text-center rounded-xl bg-slate-50 border border-dashed border-slate-200 text-slate-500 text-sm">
                    <i className="fa-regular fa-folder-open text-3xl text-slate-300 mb-2 block"></i>
                    Ingen tilmeldinger fundet i denne kategori.
                  </div>
                ) : (
                  <div className="space-y-4 max-h-[750px] overflow-y-auto pr-1">
                    {filteredBookings.map((b) => {
                      const isCancelled = b.status === 'cancelled';
                      return (
                        <div
                          key={b.id}
                          className={`p-5 rounded-2xl border transition-all ${
                            isCancelled
                              ? 'bg-slate-50/60 border-slate-200 opacity-60'
                              : 'bg-white border-slate-200 shadow-sm hover:border-[#007a53]/40'
                          }`}
                        >
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                            <div className="flex items-center gap-2">
                              <span
                                className={`px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${
                                  isCancelled
                                    ? 'bg-rose-100 text-rose-700'
                                    : 'bg-emerald-100 text-emerald-800'
                                }`}
                              >
                                {isCancelled ? 'Aflyst' : 'Bekræftet'}
                              </span>
                              <span className="text-xs text-slate-500 font-mono">
                                #{b.id}
                              </span>
                            </div>

                            <div className="text-xs font-bold text-slate-900 bg-[#007a53]/10 text-[#007a53] px-3 py-1 rounded-lg self-start sm:self-auto">
                              <i className="fa-regular fa-clock mr-1"></i>
                              {b.slot_date} kl. {b.slot_time}
                            </div>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs mb-4">
                            <div>
                              <div className="text-slate-400 font-semibold mb-0.5">Kontaktperson</div>
                              <div className="text-sm font-bold text-slate-900">{b.name}</div>
                              <div className="text-slate-600 mt-1 flex items-center gap-2">
                                <a
                                  href={`tel:${b.phone}`}
                                  className="text-[#007a53] hover:underline font-semibold flex items-center gap-1"
                                >
                                  <i className="fa-solid fa-phone text-[10px]"></i>
                                  {b.phone}
                                </a>
                                <span>·</span>
                                <a
                                  href={`mailto:${b.email}`}
                                  className="text-[#007a53] hover:underline truncate max-w-[180px] flex items-center gap-1"
                                >
                                  <i className="fa-regular fa-envelope text-[10px]"></i>
                                  {b.email}
                                </a>
                              </div>
                            </div>

                            <div>
                              <div className="text-slate-400 font-semibold mb-0.5">Deltagere & Klasse</div>
                              <div className="text-slate-800 font-medium">
                                <span className="font-bold">{b.participants} personer</span>
                                {b.grade ? ` · ${b.grade}` : ''}
                              </div>
                              <div className="text-[11px] text-slate-400 mt-1">
                                Oprettet: {new Date(b.created_at).toLocaleDateString('da-DK', {
                                  day: 'numeric',
                                  month: 'short',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                              </div>
                            </div>
                          </div>

                          {b.message ? (
                            <div className="p-3 rounded-xl bg-slate-50 border border-slate-100 text-xs text-slate-700 mb-4">
                              <span className="font-bold text-slate-900 block mb-0.5">Bemærkning fra familien:</span>
                              <p className="italic">"{b.message}"</p>
                            </div>
                          ) : null}

                          <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
                            {isCancelled ? (
                              <button
                                onClick={() => handleConfirmBooking(b.id)}
                                className="px-3 py-1.5 rounded-lg border border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-xs font-semibold transition-colors"
                              >
                                Genaktiver booking
                              </button>
                            ) : (
                              <button
                                onClick={() => handleCancelBooking(b.id)}
                                className="px-3 py-1.5 rounded-lg border border-rose-200 text-rose-600 hover:bg-rose-50 text-xs font-semibold transition-colors"
                              >
                                Aflys booking (frigiv tid)
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
